/**
 * ScGatedButton + useSCRestriction — tiny wrappers that let any page
 * disable a button with a helpful tooltip when the caller is a Sales
 * Coordinator and the action isn't permitted on this record / tenant.
 *
 * The wrapper is deliberately permissive for every non-SC role: it renders
 * the child button exactly as given with zero behavioural change. SCs see
 * the button disabled (greyed out + no onClick) and a Radix tooltip over
 * it explaining why.
 *
 * Usage patterns:
 *
 *   // 1. Ownership-based gate on an Edit button for a lead.
 *   <ScGatedButton action="edit" record={lead} onClick={openEdit}>
 *     Edit lead
 *   </ScGatedButton>
 *
 *   // 2. Flag-based gate on a CSV export (regardless of ownership).
 *   <ScGatedButton action="export" flag="can_export_csv" onClick={exportCsv}>
 *     Export CSV
 *   </ScGatedButton>
 *
 *   // 3. Mixed: a bulk reassign requires BOTH the flag AND manager context.
 *   <ScGatedButton action="reassign" flag="can_bulk_reassign" onClick={doIt}>
 *     Bulk reassign
 *   </ScGatedButton>
 *
 *   // 4. Direct hook usage when you can't use the wrapper (e.g. you need
 *   //    to gate a <select> or a form submit).
 *   const { disabled, tooltip } = useSCRestriction({ action: 'delete', record });
 */

import React from 'react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import useSCPermissions from '../hooks/useSCPermissions';

// Which SC flag each action maps to by default. Callers can still override
// with the `flag` prop when the semantics differ (e.g. "share link" vs
// "export CSV").
const DEFAULT_FLAG_FOR_ACTION = {
  export: 'can_export_csv',
  reassign: 'can_bulk_reassign',
  'bulk-reassign': 'can_bulk_reassign',
  'bulk-delete': 'can_bulk_delete',
  share: 'can_use_share_links',
};

const DEFAULT_LABEL_FOR_ACTION = {
  edit: 'edit this item',
  delete: 'delete this item',
  export: 'export data',
  reassign: 'reassign records',
  'bulk-reassign': 'reassign records in bulk',
  'bulk-delete': 'delete records in bulk',
  share: 'generate share links',
};


/**
 * Hook form — returns `{ disabled, tooltip, isSC }`. Non-SC callers always
 * get `{ disabled: false, tooltip: '' }` so the host component can simply
 * spread the return value onto its button without a role check.
 */
export function useSCRestriction({ action, record, flag } = {}) {
  const perms = useSCPermissions();

  if (!perms.isSC) {
    return { disabled: false, tooltip: '', isSC: false };
  }

  // Ownership check — only runs when a record is supplied (edit/delete).
  if (record !== undefined && (action === 'edit' || action === 'delete')) {
    if (!perms.canEditRecord(record)) {
      return {
        disabled: true,
        tooltip: `View-only — contact President for access to ${DEFAULT_LABEL_FOR_ACTION[action] || 'edit this item'}`,
        isSC: true,
      };
    }
  }

  // Flag check — either explicit `flag` prop or derived from action.
  const effectiveFlag = flag || DEFAULT_FLAG_FOR_ACTION[action];
  if (effectiveFlag && !perms.flags[effectiveFlag]) {
    return {
      disabled: true,
      tooltip: `View-only — contact President to unlock ${(DEFAULT_LABEL_FOR_ACTION[action] || effectiveFlag).toString()}`,
      isSC: true,
    };
  }

  return { disabled: false, tooltip: '', isSC: true };
}


/**
 * ScGatedButton — thin wrapper around a native <button> (or any element
 * accepting `disabled`+`onClick`). Renders a Radix tooltip over the
 * disabled element so SCs get a clear reason.
 *
 * Props:
 *   action:   'edit' | 'delete' | 'export' | 'reassign' | 'bulk-reassign'
 *             | 'bulk-delete' | 'share'     (required)
 *   record:   dict — present on ownership-gated actions.
 *   flag:     overrides DEFAULT_FLAG_FOR_ACTION.
 *   as:       component to render as (default 'button'). Use for <a>/<Link>.
 *   children, onClick, className, ...rest — forwarded.
 */
export function ScGatedButton({
  action,
  record,
  flag,
  as: Comp = 'button',
  onClick,
  disabled: disabledFromParent = false,
  className = '',
  children,
  tooltipSide = 'top',
  ...rest
}) {
  const { disabled: scDisabled, tooltip } = useSCRestriction({ action, record, flag });
  const isDisabled = disabledFromParent || scDisabled;

  const btn = (
    <Comp
      {...rest}
      disabled={isDisabled}
      aria-disabled={isDisabled || undefined}
      data-sc-gated={scDisabled ? action : undefined}
      className={`${className} ${scDisabled ? 'opacity-50 cursor-not-allowed' : ''}`.trim()}
      onClick={(e) => {
        if (isDisabled) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        onClick?.(e);
      }}
    >
      {children}
    </Comp>
  );

  if (!scDisabled) return btn;

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        {/* Radix needs a span wrapper to receive events over a disabled element */}
        <TooltipTrigger asChild>
          <span className="inline-flex" style={{ cursor: 'not-allowed' }}>{btn}</span>
        </TooltipTrigger>
        <TooltipContent side={tooltipSide} className="max-w-xs text-xs leading-snug">
          {tooltip}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export default ScGatedButton;
